import { SOUND } from './constants';
export class SoundManager {
    constructor() {
        this.enabled = false;
        this.volume = 0.5;
        this.audioContext = null;
        this.buffers = new Map();
        this.initialized = false;
        this.volume = SOUND.VOLUME;
    }
    /**
     * Initialize audio context on first user interaction
     * Browsers block auto-play until user interacts
     */
    init() {
        if (this.initialized)
            return;
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) {
                this.audioContext = new AudioContextClass();
                this.initialized = true;
                this.loadSounds();
            }
        }
        catch (e) {
            console.warn('AudioContext not supported', e);
        }
    }
    setEnabled(enabled) {
        this.enabled = enabled;
        if (enabled && !this.initialized) {
            this.init();
        }
        if (enabled && this.audioContext?.state === 'suspended') {
            this.audioContext.resume().catch(() => {
                /* ignore autoplay / resume failures */
            });
        }
    }
    setVolume(volume) {
        this.volume = Math.max(0, Math.min(1, volume));
    }
    /**
     * Generate simple synthesized sounds to avoid loading external assets initially
     * Later we can load real audio files if needed
     */
    loadSounds() {
        if (!this.audioContext)
            return;
        // We'll generate sounds procedurally for now
        // This avoids 404s for missing audio files
    }
    playClick(type = 'default') {
        if (!this.enabled || !this.audioContext)
            return;
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume().catch(() => {
                /* ignore */
            });
        }
        try {
            const oscillator = this.audioContext.createOscillator();
            const gainNode = this.audioContext.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(this.audioContext.destination);
            const now = this.audioContext.currentTime;
            switch (type) {
                case 'error':
                    // Error sound: low pitch, short buzz
                    oscillator.type = 'sawtooth';
                    oscillator.frequency.setValueAtTime(150, now);
                    oscillator.frequency.exponentialRampToValueAtTime(100, now + 0.1);
                    gainNode.gain.setValueAtTime(this.volume * 0.8, now);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
                    oscillator.start(now);
                    oscillator.stop(now + 0.15);
                    break;
                case 'success':
                    // Success/Finish sound: high pitch, pleasant chord
                    this.playTone(600, 'sine', 0.1, now);
                    this.playTone(800, 'sine', 0.1, now + 0.05);
                    this.playTone(1200, 'sine', 0.2, now + 0.1);
                    break;
                case 'default':
                default:
                    // Keypress: simple mechanical click
                    // Emulate a mechanical switch sound using white noise burst + short tone
                    // 1. High frequency 'tick'
                    oscillator.type = 'triangle';
                    oscillator.frequency.setValueAtTime(800, now);
                    oscillator.frequency.exponentialRampToValueAtTime(300, now + 0.03);
                    gainNode.gain.setValueAtTime(this.volume * 0.4, now);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
                    oscillator.start(now);
                    oscillator.stop(now + 0.05);
                    break;
            }
        }
        catch (e) {
            // Ignore audio errors
        }
    }
    playTone(freq, type, duration, startTime) {
        if (!this.audioContext)
            return;
        const os = this.audioContext.createOscillator();
        const gn = this.audioContext.createGain();
        os.type = type;
        os.frequency.value = freq;
        os.connect(gn);
        gn.connect(this.audioContext.destination);
        gn.gain.setValueAtTime(this.volume * 0.5, startTime);
        gn.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        os.start(startTime);
        os.stop(startTime + duration);
    }
}
